// =============================================================================
// Legacy order intake — back-enter a pre-go-live paper order
// =============================================================================
// The system went live 1 Jul 2026. Orders written on the paper invoice book
// before that, whose delivery falls on or after it, are recorded here so the
// customer keeps quoting the manual invoice number they already hold while
// production, delivery, payments and the VAT invoice run through the system.
//
// Deliberately NOT the New Order wizard with a checkbox: this path relaxes the
// stock, capacity and past-date guards, so it lives on its own page behind its
// own permission (owner + admin, or a per-user grant for the backfill). It's a
// flat form rather than a stepper — the operator is copying from a paper book,
// not selling, and wants every field visible at once.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { FulfillmentType, Region } from '@kalachand/shared';
import { LEGACY_INTAKE_CUTOFF_ISO } from '@kalachand/shared';
import { apiGet, apiPost } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useSubmitGuard } from '../../lib/useSubmitGuard';
import { PageScaffold } from '../../components/PageScaffold';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { useToast } from '../../components/Toast';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { CustomSizeModal, type CustomSizeResult } from './CustomSizeModal';
import { CustomerPicker, ProductPicker, type Customer, type Product } from './NewOrderPage';

const REGIONS: Region[] = ['north', 'south', 'east', 'west', 'center'];

/** What state the goods for a line were in when the system went live. */
type LineState = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE';

const LINE_STATE_LABEL: Record<LineState, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  DONE: 'Already made',
};

interface Shop {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface LegacyLine {
  key: string;
  /** The product id sent to the API — the matched standard variant for a custom size. */
  productId: string;
  sku: string;
  name: string;
  qty: number;
  unitPrice: number;
  discount: number;
  lineState: LineState;
  // Custom size (bespoke W×L on a made-to-order mattress). unitPrice is the
  // server-derived ladder price; the server recomputes it on submit either way.
  customWidthCm?: number;
  customLengthCm?: number;
  selectedColor?: string | null;
  selectedSideColor?: string | null;
  selectedFabric?: string | null;
  selectedDensity?: string | null;
}

interface Shortfall {
  productId: string;
  sku: string;
  name: string;
  onHand: number;
}

interface IntakeResult {
  order: { id: string; orderNo: string; number: string };
  shortfalls: Shortfall[];
  legacyDepositRecorded: string;
}

export function LegacyIntakePage(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const guard = useSubmitGuard();

  const shops = useQuery({ queryKey: ['shops'], queryFn: () => apiGet<Shop[]>('/shops') });
  const shopList = useMemo(() => (shops.data ?? []).filter((s) => s.isActive), [shops.data]);

  const [shopId, setShopId] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [lines, setLines] = useState<LegacyLine[]>([]);
  const [customFor, setCustomFor] = useState<Product | null>(null);

  const [legacyRef, setLegacyRef] = useState('');
  const [legacyOrderDate, setLegacyOrderDate] = useState('');
  const [deposit, setDeposit] = useState('0');
  const [depositMethod, setDepositMethod] = useState('cash');

  const [fulfillmentType, setFulfillmentType] = useState<FulfillmentType>('delivery');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [region, setRegion] = useState<Region | ''>('');
  const [notes, setNotes] = useState('');

  const [shortfalls, setShortfalls] = useState<Shortfall[]>([]);

  const effectiveShopId = shopId || user?.activeShopId || user?.homeShopId || shopList[0]?.id || '';

  const total = lines.reduce((s, l) => s + l.qty * l.unitPrice - l.discount, 0);

  const addProduct = (p: Product): void => {
    const price = Number(p.salePrice ?? p.basePrice);
    setLines((prev) => [
      ...prev,
      {
        key: `${p.id}-${prev.length}`,
        productId: p.id,
        sku: p.sku,
        name: p.name,
        qty: 1,
        unitPrice: price,
        discount: 0,
        lineState: 'NOT_STARTED',
      },
    ]);
  };

  const addCustom = (r: CustomSizeResult): void => {
    setLines((prev) => [
      ...prev,
      {
        key: `custom-${prev.length}-${r.matchedVariantId}`,
        productId: r.matchedVariantId,
        sku: `${r.widthCm}×${r.lengthCm}`,
        name: `${r.modelName} — custom ${r.widthCm}×${r.lengthCm} cm`,
        qty: 1,
        unitPrice: r.unitPrice,
        discount: 0,
        lineState: 'NOT_STARTED',
        customWidthCm: r.widthCm,
        customLengthCm: r.lengthCm,
        selectedColor: r.colorOptions[0] ?? null,
        selectedSideColor: r.sideColorOptions[0] ?? null,
        selectedFabric: r.fabricOptions[0] ?? null,
        selectedDensity: r.densityOptions[0] ?? null,
      },
    ]);
    setCustomFor(null);
  };

  const patchLine = (key: string, patch: Partial<LegacyLine>): void => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  // Client-side gate mirroring the server's. The server fence is the real one —
  // this just avoids a round trip and explains the rule where it's being broken.
  const dateIsLegacy = !!legacyOrderDate && legacyOrderDate < LEGACY_INTAKE_CUTOFF_ISO;
  const problems: string[] = [];
  if (!effectiveShopId) problems.push('Pick a shop.');
  if (!customer) problems.push('Pick a customer.');
  if (lines.length === 0) problems.push('Add at least one line.');
  if (!legacyRef.trim()) problems.push('Enter the manual invoice number.');
  if (!legacyOrderDate) problems.push('Enter the date on the paper invoice.');
  else if (!dateIsLegacy) {
    problems.push(
      `The paper date must be before ${LEGACY_INTAKE_CUTOFF_ISO} — anything from go-live onwards is a normal order.`,
    );
  }
  if (fulfillmentType === 'delivery' && !deliveryAddress.trim()) {
    problems.push('Delivery orders need an address.');
  }
  if (fulfillmentType === 'delivery' && !region) problems.push('Pick a delivery region.');

  const submit = useMutation({
    mutationFn: () =>
      apiPost<IntakeResult>('/orders/legacy-intake', {
        shopId: effectiveShopId,
        customerId: customer!.id,
        fulfillmentType,
        lines: lines.map((l) => ({
          productId: l.productId,
          qty: l.qty,
          unitPrice: l.unitPrice,
          discount: l.discount,
          ...(l.customWidthCm
            ? {
                customWidthCm: l.customWidthCm,
                customLengthCm: l.customLengthCm,
                selectedColor: l.selectedColor,
                selectedSideColor: l.selectedSideColor,
                selectedFabric: l.selectedFabric,
                selectedDensity: l.selectedDensity,
              }
            : {}),
        })),
        ...(fulfillmentType === 'delivery'
          ? { deliveryAddress, region: region || undefined }
          : {}),
        ...(deliveryDate ? { deliveryDate } : {}),
        notes: notes.trim() || undefined,
        legacyRef: legacyRef.trim(),
        legacyOrderDate,
        legacyDeposit: Number(deposit) || 0,
        legacyDepositMethod: depositMethod,
        lineStates: lines.map((l) => l.lineState),
      }),
    onSuccess: (r) => {
      if (r.shortfalls.length > 0) {
        // Deliberately not a blocking error: the order IS recorded. This is the
        // "make or buy these" worklist — units this paper order is owed that
        // were already sold out of the system since go-live.
        setShortfalls(r.shortfalls);
        toast.show(`Recorded ${r.order.orderNo} — ${r.shortfalls.length} product(s) now short`, 'info');
      } else {
        toast.show(`Recorded ${r.order.orderNo}`, 'success');
        navigate(`/orders/${r.order.id}`);
      }
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Could not record the order.';
      toast.show(msg, 'error');
    },
    onSettled: guard.release,
  });

  return (
    <PageScaffold
      back={{ to: '/orders', label: 'Orders' }}
      title="Back-enter a paper order"
      subtitle={`Orders written before ${LEGACY_INTAKE_CUTOFF_ISO} — the manual invoice number is kept`}
      contentClassName="space-y-4"
    >
      {shortfalls.length > 0 && (
        <Card
          title="Stock now short"
          subtitle="These were already sold out of the system before this order was recorded — make or order them"
          className="border-warning/40"
        >
          <ul className="text-sm space-y-1">
            {shortfalls.map((s) => (
              <li key={s.productId} className="flex justify-between">
                <span className="text-ink">
                  <span className="kc-mono text-ink-3">{s.sku}</span> {s.name}
                </span>
                <span className="text-warning font-medium">{s.onHand}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <Button variant="secondary" onClick={() => setShortfalls([])}>
              Enter the next order
            </Button>
          </div>
        </Card>
      )}

      <Card title="The paper invoice">
        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            id="legacy-ref"
            label="Manual invoice no."
            hint="Exactly as written on the customer's copy — this becomes the order's document number"
            value={legacyRef}
            onChange={(e) => setLegacyRef(e.target.value)}
          />
          <Input
            id="legacy-date"
            type="date"
            label="Date on the invoice"
            max={LEGACY_INTAKE_CUTOFF_ISO}
            value={legacyOrderDate}
            onChange={(e) => setLegacyOrderDate(e.target.value)}
          />
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Shop</span>
            <select
              className="kc-input"
              value={effectiveShopId}
              onChange={(e) => setShopId(e.target.value)}
            >
              {shopList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      <Card
        title="Deposit already collected"
        subtitle="Cash taken before go-live. Recorded against the order but NOT posted to the accounts — that money is already in the opening balance."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            id="legacy-deposit"
            type="number"
            min="0"
            step="0.01"
            label="Amount (Rs)"
            value={deposit}
            onChange={(e) => setDeposit(e.target.value)}
          />
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Method</span>
            <select
              className="kc-input"
              value={depositMethod}
              onChange={(e) => setDepositMethod(e.target.value)}
            >
              {['cash', 'juice', 'emtel', 'myt', 'bank_transfer', 'card', 'cheque', 'other'].map(
                (m) => (
                  <option key={m} value={m}>
                    {m.replace('_', ' ')}
                  </option>
                ),
              )}
            </select>
          </label>
        </div>
      </Card>

      <CustomerPicker value={customer} onChange={setCustomer} />

      <ProductPicker onAdd={addProduct} onCustom={setCustomFor} />

      {lines.length > 0 && (
        <Card title="Lines" subtitle="Mark what was already made — those don't book a production day">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3">
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 px-2 w-20">Qty</th>
                  <th className="py-2 px-2 w-28">Unit price</th>
                  <th className="py-2 px-2 w-28">Discount</th>
                  <th className="py-2 px-2 w-36">Production</th>
                  <th className="py-2 pl-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.key} className="border-t border-rule-2">
                    <td className="py-2 pr-3">
                      <div className="text-ink">{l.name}</div>
                      <div className="text-xs kc-mono text-ink-3">{l.sku}</div>
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        min="1"
                        className="kc-input"
                        value={l.qty}
                        onChange={(e) =>
                          patchLine(l.key, { qty: Math.max(1, Number(e.target.value) || 1) })
                        }
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="kc-input"
                        value={l.unitPrice}
                        onChange={(e) => patchLine(l.key, { unitPrice: Number(e.target.value) || 0 })}
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="kc-input"
                        value={l.discount}
                        onChange={(e) => patchLine(l.key, { discount: Number(e.target.value) || 0 })}
                      />
                    </td>
                    <td className="py-2 px-2">
                      <select
                        className="kc-input"
                        value={l.lineState}
                        onChange={(e) =>
                          patchLine(l.key, { lineState: e.target.value as LineState })
                        }
                      >
                        {(Object.keys(LINE_STATE_LABEL) as LineState[]).map((s) => (
                          <option key={s} value={s}>
                            {LINE_STATE_LABEL[s]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pl-2">
                      <button
                        type="button"
                        className="text-ink-3 hover:text-negative"
                        onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                        aria-label={`Remove ${l.name}`}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-end text-sm">
            <span className="text-ink-3 mr-3">Order total (VAT incl.)</span>
            <Money value={total} className="font-medium text-ink" />
          </div>
        </Card>
      )}

      <Card
        title="Fulfilment"
        subtitle="Route and capacity rules are relaxed here — record what the paper says, including a date that has already passed"
      >
        <div className="grid gap-3">
          <div className="flex gap-2">
            {(['delivery', 'pickup'] as FulfillmentType[]).map((t) => (
              <Button
                key={t}
                variant={fulfillmentType === t ? 'primary' : 'secondary'}
                onClick={() => setFulfillmentType(t)}
              >
                {t === 'delivery' ? 'Delivery' : 'Pickup'}
              </Button>
            ))}
          </div>

          {fulfillmentType === 'delivery' && (
            <>
              <AddressAutocomplete
                label="Delivery address"
                value={deliveryAddress}
                onChange={setDeliveryAddress}
              />
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                  Region
                </span>
                <select
                  className="kc-input"
                  value={region}
                  onChange={(e) => setRegion(e.target.value as Region)}
                >
                  <option value="">Choose a region…</option>
                  {REGIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          <Input
            id="legacy-delivery-date"
            type="date"
            label="Delivery date"
            hint="The date promised on the paper order. A past date is allowed here — it isn't anywhere else."
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
          />

          <Input
            id="legacy-notes"
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </Card>

      <Card>
        {problems.length > 0 && (
          <ul className="mb-3 text-sm text-ink-3 list-disc pl-5 space-y-0.5">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        <Button
          disabled={problems.length > 0}
          loading={submit.isPending}
          onClick={() => guard.run(() => submit.mutate())}
        >
          Record this order
        </Button>
      </Card>

      {customFor && (
        <CustomSizeModal
          modelId={customFor.modelId ?? ''}
          fallbackName={customFor.name}
          onClose={() => setCustomFor(null)}
          onConfirm={addCustom}
        />
      )}
    </PageScaffold>
  );
}
